import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CustomerDetailsConsumptionApiService } from '../../customers/components/customer-details-consumption/customer-details-consumption.api.service';
import { ConsumptionAnalyticsActions } from './consumption-analytics.actions';
import { catchError, exhaustMap, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs';

import {
  selectCommodity,
  selectDateRange,
  selectFlowDirectionFilter,
  selectInterval,
  selectPreviousPeriod,
  selectReportGroupFilter,
  selectTableState,
  selectUnitOfMeasure,
  selectWidgetConsumptionsFilters
} from './consumption-analytics.reducer';
import { FlowDirection } from '@portal/shared/types/filter.types';
import { ConsumptionWidgetService } from '../../components/consumption-widget/consumption-widget.service';
import { PreviousPeriodService } from '../../shared/previous-period.serivce';
import {
  ConsumptionWidgetPayloadOverview,
  GetConsumptionPayload,
  GroupElectricConsumptionDtoFieldName
} from '../../shared/consumption.types';
import { ReportGroupFilterApiService } from '../../../../services/api/report-group-filter/report-group-filter-api.service';
import { CommodityType, DatepickerRange, RangeDatepickerComponent } from '@ui/aos-lib';
import {
  buildUnitsOfMeasure,
  UnitsOfMeasure
} from '../../customers/components/customer-details-consumption/unit-of-measure/units-of-measure.utils';
import { ConsumptionAnalyticsService } from './consumption-analytics.service';
import { DateUtil } from '@portal/shared/utils/date-util';
import { TotalConsumptionUIService } from '../consumption-analytics.service';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { ConsumptionAnalyticsDateRangeService } from '../consumption-analytics.date-range.service';
import { Commodity } from '@portal/shared/types/commodity.types';
import {
  GetChartDataItem,
  GetChartDataResponse
} from 'src/app/pages/equipment/devices/components/device-details/device-details-metrics/meter/meter-consumption-tab-content/meter-consumption-tab-content.types';
import { DeviceTimeSeries } from 'src/app/pages/equipment/health-monitor/health-api.types';

const applicable = 'Consumption Analytics';

export const loadData$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.setFilters, ConsumptionAnalyticsActions.initFilters),
      withLatestFrom(store.select(selectCommodity)),
      mergeMap(([action, commodity]) =>
        commodity === Commodity.Electricity
          ? [
              ConsumptionAnalyticsActions.setUnitOfMeasureModel({
                model: buildUnitsOfMeasure({
                  service: CommodityType.Electric,
                  originalInputUnit: 'kWh' as UnitsOfMeasure
                }),
                reportGroupUID: (action as { reportGroupUID: string })?.reportGroupUID as string
              }),
              ConsumptionAnalyticsActions.getConsumptionsElectric(),
              ConsumptionAnalyticsActions.getConsumptionReadsElectric()
            ]
          : [
              ConsumptionAnalyticsActions.getWidgetConsumptions({
                selectedUom:
                  action.type === ConsumptionAnalyticsActions.initFilters.type ? action.filtersState?.uom : undefined
              })
            ]
      )
    ),
  { functional: true }
);

export const refresh$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.refreshData),
      map(() => ConsumptionAnalyticsActions.getPersistedFiltersState())
    ),
  { functional: true }
);

export const triggerConsumptions$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getWidgetConsumptionsSuccess),
      map(() => ConsumptionAnalyticsActions.getConsumptions())
    ),
  { functional: true }
);

export const triggerConsumptionReads$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getConsumptionsSuccess),
      map(() => ConsumptionAnalyticsActions.getConsumptionReads())
    ),
  { functional: true }
);

export const onUnitOfMeasureChange$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.buildEntitiesWithUom),
      mergeMap(() => {
        return [
          ConsumptionAnalyticsActions.buildWidgetWithUom(),
          ConsumptionAnalyticsActions.buildChartWithUom(),
          ConsumptionAnalyticsActions.buildTableWithUom()
        ];
      })
    ),
  { functional: true }
);

export const loadWidgetConsumptions$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    consumptionWidgetService = inject(ConsumptionWidgetService),
    tzService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getWidgetConsumptions),
      withLatestFrom(store.select(selectWidgetConsumptionsFilters), store.select(selectCommodity)),
      filter(([, commodity]) => commodity !== null),
      exhaustMap(([{ selectedUom }, payload, commodity]) => {
        if (commodity === Commodity.Electricity) {
          //todo: fetch electric consumptions instead
          return of();
        }
        const { start, end } = tzService.getDatepickerRange({
          start: payload!.startDateUtc,
          end: payload!.endDateUtc
        });
        const { flowDirection, ...payloadWithoutFlowDirection } = payload!;
        const updatedPayload = {
          ...payloadWithoutFlowDirection,
          endDateUtc: DateUtil.addOneDayToString(end),
          startDateUtc: DateUtil.toISOString(start)
        };

        return consumptionApiService.getConsumptionOverview(updatedPayload as ConsumptionWidgetPayloadOverview).pipe(
          mergeMap(widget => {
            return [
              ConsumptionAnalyticsActions.getWidgetConsumptionsSuccess({
                widget: consumptionWidgetService.getOverviewWidgetConfig(widget)
              }),
              ConsumptionAnalyticsActions.setUnitOfMeasureModel({
                model: buildUnitsOfMeasure({
                  service: commodity as CommodityType,
                  originalInputUnit: widget.currentPeriod.unitOfMeasure as UnitsOfMeasure,
                  ...(selectedUom && { selectedItemUnit: selectedUom })
                }),
                reportGroupUID: payload!.reportGroupUIDs[0]
              }),
              ConsumptionAnalyticsActions.buildWidgetWithUom()
            ];
          }),
          catchError((error: unknown) =>
            of(ConsumptionAnalyticsActions.getWidgetConsumptionsFailure({ error: String(error) }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadConsumptions$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    previousPeriodService = inject(PreviousPeriodService),
    tzService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getConsumptions),
      withLatestFrom(
        store.select(selectWidgetConsumptionsFilters),
        store.select(selectInterval),
        store.select(selectCommodity)
      ),
      filter(([, , , commodity]) => commodity !== null),
      exhaustMap(([, filters, interval]) => {
        const { start, end } = tzService.getDatepickerRange({
          start: filters!.startDateUtc,
          end: filters!.endDateUtc
        });
        const payload = {
          ...filters,
          interval,
          endDateUtc: DateUtil.addOneDayToString(end),
          startDateUtc: DateUtil.toISOString(start)
        };
        return consumptionApiService.getConsumption(payload as GetConsumptionPayload).pipe(
          mergeMap(health => {
            const preparedData = previousPeriodService.prepareConsumptionData(health);
            const consumptions = previousPeriodService.overlapAxisDeviceHealthChart(preparedData[0], preparedData[1]);
            return [
              ConsumptionAnalyticsActions.getConsumptionsSuccess({ consumptions }),
              ConsumptionAnalyticsActions.buildChartWithUom()
            ];
          }),
          catchError((error: unknown) =>
            of(ConsumptionAnalyticsActions.getConsumptionsFailure({ error: String(error) }))
          )
        );
      })
    ),
  { functional: true }
);

export const loadConsumptionReads$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), consumptionService = inject(ConsumptionAnalyticsService)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getConsumptionReads, ConsumptionAnalyticsActions.setTableState),
      withLatestFrom(
        store.select(selectWidgetConsumptionsFilters),
        store.select(selectTableState),
        store.select(selectCommodity)
      ),
      filter(([, , , commodity]) => commodity !== null && commodity !== Commodity.Electricity),
      exhaustMap(([, filters, tableState]) => {
        return consumptionService.getTableData(filters!, tableState).pipe(
          mergeMap(consumptionReads => {
            return [
              ConsumptionAnalyticsActions.getConsumptionsReadsSuccess({ consumptionReads }),
              ConsumptionAnalyticsActions.buildTableWithUom()
            ];
          }),
          catchError((error: unknown) =>
            of(ConsumptionAnalyticsActions.getConsumptionsReadsFailure({ error: String(error) }))
          )
        );
      })
    ),
  { functional: true }
);

export const getFiltersState$ = createEffect(
  (actions$ = inject(Actions), totalConsumptionUIService = inject(TotalConsumptionUIService)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getPersistedFiltersState),
      exhaustMap(() =>
        totalConsumptionUIService.loadPersistedFilters().pipe(
          map(({ filters, pageSize }) =>
            ConsumptionAnalyticsActions.getReportGroupAndInitFiltersState({
              filtersState: filters,
              ...(pageSize != null && { pageSize })
            })
          ),
          catchError(() => of(ConsumptionAnalyticsActions.getReportGroupAndInitFiltersState({ filtersState: null })))
        )
      )
    ),
  { functional: true }
);

export const getReportGroupAndInitFiltersState$ = createEffect(
  (
    actions$ = inject(Actions),
    reportGroupFilterApiService = inject(ReportGroupFilterApiService),
    dateRangeService = inject(ConsumptionAnalyticsDateRangeService)
  ) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getReportGroupAndInitFiltersState),
      exhaustMap(({ filtersState, pageSize }) => {
        const dateRange = filtersState?.dateRange ?? {
          start: new Date(
            dateRangeService.shiftDateToMinusOne(
              (RangeDatepickerComponent.LAST_TWO_WEEK.getValue() as DatepickerRange).start
            )
          ),
          end: new Date(
            dateRangeService.shiftDateToMinusOne(
              (RangeDatepickerComponent.LAST_TWO_WEEK.getValue() as DatepickerRange).end
            )
          )
        };
        return reportGroupFilterApiService.getReportGroup(applicable).pipe(
          map(reportGroups =>
            ConsumptionAnalyticsActions.initFilters({
              reportGroups,
              filtersState,
              dateRange,
              ...(pageSize != null && { restoredTake: pageSize })
            })
          ),
          catchError(() => of(ConsumptionAnalyticsActions.initFilters({ reportGroups: [], filtersState, dateRange })))
        );
      })
    ),
  { functional: true }
);

export const filtersOrUomChange$ = createEffect(
  (actions$ = inject(Actions)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.setFilters, ConsumptionAnalyticsActions.buildEntitiesWithUom),
      map(() => ConsumptionAnalyticsActions.savePersistedFiltersState())
    ),
  { functional: true }
);

export const saveFiltersState$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), totalConsumptionUIService = inject(TotalConsumptionUIService)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.savePersistedFiltersState),
      withLatestFrom(
        store.select(selectReportGroupFilter),
        store.select(selectDateRange),
        store.select(selectPreviousPeriod),
        store.select(selectUnitOfMeasure),
        store.select(selectFlowDirectionFilter)
      ),
      tap(([, reportGroupFilter, dateRange, previousPeriod, unitOfMeasureModel, flowDirectionFilter]) => {
        return totalConsumptionUIService.persistFilters({
          reportGroupUID: reportGroupFilter.selected[0].value as string,
          dateRange: dateRange!,
          previousPeriod: previousPeriod!,
          uom: unitOfMeasureModel.model.selectedItem.value as string,
          flowDirection: flowDirectionFilter.selected.map(item => item.value as FlowDirection)
        });
      })
    ),
  { functional: true, dispatch: false }
);

export const loadConsumptionsElectric$ = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    consumptionApiService = inject(CustomerDetailsConsumptionApiService),
    tzService = inject(TimezoneService)
  ) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getConsumptionsElectric),
      withLatestFrom(store.select(selectWidgetConsumptionsFilters), store.select(selectCommodity)),
      filter(([, , commodity]) => commodity !== null),
      switchMap(([, filters]) => {
        const { start, end } = tzService.getDatepickerRange({
          start: filters!.startDateUtc,
          end: filters!.endDateUtc
        });
        const payload = {
          filters: [
            {
              filterType: GroupElectricConsumptionDtoFieldName.DateTimeUTC,
              filteredValues: [DateUtil.toISOString(start), DateUtil.addOneDayToString(end)]
            },
            {
              filterType: GroupElectricConsumptionDtoFieldName.ReportGroupUID,
              filteredValues: filters!.reportGroupUIDs
            },
            ...(filters?.flowDirection
              ? [
                  {
                    filterType: GroupElectricConsumptionDtoFieldName.FlowDirection,
                    filteredValues: filters.flowDirection!
                  }
                ]
              : [])
          ]
        };

        if (!filters?.flowDirection?.length) {
          return of(
            ConsumptionAnalyticsActions.getConsumptionsElectricSuccess({ consumptions: [] }),
            ConsumptionAnalyticsActions.buildChartWithUom()
          );
        }

        return consumptionApiService.getConsumptionElectric(payload).pipe(
          mergeMap(response => {
            const { unitOfMeasure, multiplier, ...consumptionEntries } = response;
            const selectedFlowDirections = filters.flowDirection!.map(direction => direction.toLowerCase());
            const consumptions = Object.entries(consumptionEntries)
              .filter(([key]) => selectedFlowDirections.includes(key))
              .map(([key, value]: [string, GetChartDataItem[]]) => {
                return {
                  type: key as keyof GetChartDataResponse,
                  health: {
                    data: value.map((item: GetChartDataItem): DeviceTimeSeries => ({ x: item.date, y: item.value })),
                    type: key as keyof GetChartDataResponse,
                    unitOfMeasure: 'kWh',
                    multiplier: ''
                  },
                  config: []
                };
              });
            return of(
              ConsumptionAnalyticsActions.getConsumptionsElectricSuccess({ consumptions }),
              ConsumptionAnalyticsActions.buildChartWithUom()
            );
          }),
          catchError(error => of(ConsumptionAnalyticsActions.getConsumptionsFailure({ error: String(error?.error) })))
        );
      })
    ),
  { functional: true }
);

export const loadConsumptionReadsElectric$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), consumptionService = inject(ConsumptionAnalyticsService)) =>
    actions$.pipe(
      ofType(ConsumptionAnalyticsActions.getConsumptionReadsElectric, ConsumptionAnalyticsActions.setTableState),
      withLatestFrom(
        store.select(selectWidgetConsumptionsFilters),
        store.select(selectTableState),
        store.select(selectCommodity)
      ),
      filter(([, , , commodity]) => commodity !== null && commodity === Commodity.Electricity),
      switchMap(([, filters, tableState]) => {
        if (!filters?.flowDirection?.length) {
          return of(
            ConsumptionAnalyticsActions.getConsumptionsReadsElectricSuccess({
              consumptionReads: { data: [], total: 0 }
            })
          );
        }

        return consumptionService.getTableDataElectric(filters!, tableState).pipe(
          mergeMap(consumptionReads => [
            ConsumptionAnalyticsActions.getConsumptionsReadsElectricSuccess({ consumptionReads })
          ]),
          catchError(error =>
            of(
              ConsumptionAnalyticsActions.getConsumptionsReadsElectricFailure({ error: String(error?.error as string) })
            )
          )
        );
      })
    ),
  { functional: true }
);
